import { useRef, useState, type ReactNode } from 'react';
import { Play, RotateCw, Square } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { ResourceRowAction } from '@/components/resources/ResourceList';
import { containerActions, isDisruptiveContainerAction, type ContainerAction } from '@/lib/containerActions';
import type { Container } from '@/types/container';

const COPY: Record<ContainerAction, { title: string; confirmLabel: string; description: (n: string) => string; success: string; failure: string }> = {
  start: { title: 'Start container?', confirmLabel: 'Start', description: (n) => `Start '${n}'?`, success: 'Container started', failure: 'Failed to start container' },
  stop: { title: 'Stop container?', confirmLabel: 'Stop', description: (n) => `Stop '${n}'? Its processes will be stopped.`, success: 'Container stopped', failure: 'Failed to stop container' },
  restart: { title: 'Restart container?', confirmLabel: 'Restart', description: (n) => `Restart '${n}'? It will be briefly unavailable.`, success: 'Container restarted', failure: 'Failed to restart container' },
};

const CALLS: Record<ContainerAction, (id: number) => Promise<void>> = {
  start: (id) => api.containers.start(id),
  stop: (id) => api.containers.stop(id),
  restart: (id) => api.containers.restart(id),
};

const NA = 'Not available for a container in this state';

const DEFS: { action: ContainerAction; label: string; icon: ResourceRowAction<Container>['icon']; reason: string }[] = [
  { action: 'start', label: 'Start', icon: Play, reason: NA },
  { action: 'stop', label: 'Stop', icon: Square, reason: NA },
  { action: 'restart', label: 'Restart', icon: RotateCw, reason: NA },
];

const busyKey = (id: number, action: ContainerAction) => `${id}:${action}`;

interface Pending {
  action: 'stop' | 'restart';
  container: Container;
  open: boolean;
}

/** Row actions for the containers list: Start runs at once; Stop/Restart confirm first. In-flight requests are guarded per container+action. */
export function useContainerActions({ onChanged }: { onChanged: () => void }): { actions: ResourceRowAction<Container>[]; dialogs: ReactNode } {
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState<string[]>([]);
  const inFlight = useRef<Set<string>>(new Set());

  const execute = async (action: ContainerAction, container: Container) => {
    const key = busyKey(container.id, action);
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    setBusy((prev) => [...prev, key]);
    try {
      await CALLS[action](container.id);
      toast.success(COPY[action].success);
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : COPY[action].failure);
    } finally {
      inFlight.current.delete(key);
      setBusy((prev) => prev.filter((k) => k !== key));
      setPending((p) => (p && p.container.id === container.id && p.action === action ? { ...p, open: false } : p));
    }
    onChanged(); // state may have changed even on failure
  };

  const actions: ResourceRowAction<Container>[] = DEFS.map((d) => ({
    id: d.action,
    label: (c) => `${d.label} ${c.name}`,
    icon: d.icon,
    destructive: d.action === 'stop',
    enabled: (c) => containerActions(c.status).includes(d.action),
    disabledReason: () => d.reason,
    busy: (c) => busy.includes(busyKey(c.id, d.action)),
    run: (c) => {
      if (isDisruptiveContainerAction(d.action)) setPending({ action: d.action, container: c, open: true });
      else void execute(d.action, c);
    },
  }));

  const loading = pending ? busy.includes(busyKey(pending.container.id, pending.action)) : false;
  const dialogs = pending ? (
    <ConfirmDialog
      open={pending.open}
      onOpenChange={(open) => setPending((prev) => (prev ? { ...prev, open } : prev))}
      title={COPY[pending.action].title}
      description={COPY[pending.action].description(pending.container.name)}
      confirmLabel={COPY[pending.action].confirmLabel}
      destructive={pending.action === 'stop'}
      loading={loading}
      onConfirm={() => execute(pending.action, pending.container)}
    />
  ) : null;

  return { actions, dialogs };
}
